import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import './ProjectView.css';
import FileExplorer from './FileExplorer/FileExplorer';
import { getVirtualTourUrl, getVirtualTourCode } from '../../../utils/virtualTourMapping';
import fileService from '../../../services/fileService';
import viewerPresenceService from '../../../services/viewerPresenceService';
import ActiveViewersBadge from '../ActiveViewersBadge/ActiveViewersBadge';
import ManageProjectUsersModal from '../ProjectsList/ProjectCard/ManageProjectUsersModal';
import LeaveProjectModal from '../ProjectsList/ProjectCard/LeaveProjectModal';
import RenameProjectModal from '../ProjectsList/ProjectCard/RenameProjectModal';
import RSAModelAnalysis from '../../Sidebar/RSAAnalysis/RSAModelAnalysis/RSAModelAnalysis';
import RSAExistingAnalysis from '../../Sidebar/RSAAnalysis/RSAExistingAnalysis/RSAExistingAnalysis';
import PermissionGate from '../../PermissionGate/PermissionGate';
import { authHeaders } from '../../../utils/authHeader';
import { API_BASE_URL, ENVIRONMENT } from '../../../API/api';
import { getDeepLinkRunFromSearch, resolveInitialRsaTab } from '../../../utils/rsaRouteParams';
import { clearProjectFileExplorerCache } from '../../../utils/fileExplorerCache';

const RSA_VIEW_QUERY_KEY = 'rsa_view';
const RSA_TAB_QUERY_KEY = 'rsa_tab';

const getRsaViewModeFromSearch = (search = '') => {
  const params = new URLSearchParams(search);
  const rawView = String(params.get(RSA_VIEW_QUERY_KEY) || '').trim().toLowerCase();
  return rawView === 'analysis' ? 'analysis' : 'files';
};

const getRsaTabFromSearch = (search = '') => {
  const params = new URLSearchParams(search);
  return params.get(RSA_TAB_QUERY_KEY) || null;
};

const ProjectView = ({ project, projectData, onBack, onViewProject }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isRsaProject =
    project?.ProjectType === 'rsa-existing' || project?.ProjectType === 'rsa-model';
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const folderId = params.get('folder');
    return folderId ? { id: folderId, name: '' } : null;
  });
  const [currentPath, setCurrentPath] = useState([{ id: 'root', name: project?.Name || 'Project Files' }]);
  const [hasSelectedFiles, setHasSelectedFiles] = useState(false);
  const [canDeleteSelection, setCanDeleteSelection] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showLeaveProjectModal, setShowLeaveProjectModal] = useState(false);
  const [showAnalysisFlyout, setShowAnalysisFlyout] = useState(false);
  const [flyoutFixedStyle, setFlyoutFixedStyle] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [displayProjectName, setDisplayProjectName] = useState(project?.Name || 'Project Files');
  // rsaViewMode is derived strictly from the URL — single source of truth.
  // Storing it as state caused stale-value races where a child component
  // (RSAAnalysis) could render for one frame and rewrite the URL via
  // onActiveMainTabChange before a sync-effect could correct the state.
  const rsaViewMode = React.useMemo(() => {
    if (!isRsaProject) return 'files';
    const params = new URLSearchParams(location.search);
    // Explicit rsa_view in URL always wins
    if (params.has(RSA_VIEW_QUERY_KEY)) return getRsaViewModeFromSearch(location.search);
    // ?folder=X without explicit rsa_view means the user wants files view
    if (params.get('folder')) return 'files';
    // Both RSA model and rsa-existing default to analysis view
    if (project?.ProjectType === 'rsa-model' || project?.ProjectType === 'rsa-existing') return 'analysis';
    return getRsaViewModeFromSearch(location.search);
  }, [isRsaProject, location.search, project?.ProjectType]);
  const [preloadedRsaSetup, setPreloadedRsaSetup] = useState(null);
  const [preloadedRsaSetupFolderId, setPreloadedRsaSetupFolderId] = useState(null);
  const [preloadedRsaSetupFolderName, setPreloadedRsaSetupFolderName] = useState(null);
  const [preloadRsaSetupLoading, setPreloadRsaSetupLoading] = useState(false);
  const [rsaResyncLoading, setRsaResyncLoading] = useState(false);
  const [isLeavingProject, setIsLeavingProject] = useState(false);
  const fileExplorerRef = useRef(null);
  const headerMenuRef = useRef(null);
  const flyoutTimerRef = useRef(null);
  const analysisBtnRef = useRef(null);
  const routeRsaTab = getRsaTabFromSearch(location.search);
  // Dashboard deep link: /projects/<id>?run=<run_id> opens the RSA analysis
  // view on the Dashboard tab with that run selected (RSAAnalysis consumes
  // the id and strips the param). An explicit rsa_tab still wins.
  const deepLinkRunId = getDeepLinkRunFromSearch(location.search);
  const initialRsaTab = resolveInitialRsaTab(location.search, 'upload');
  const currentUserEmail = (
    sessionStorage.getItem('userEmail')
    || localStorage.getItem('userEmail')
    || ''
  ).trim().toLowerCase();

  const updateRsaRouteState = React.useCallback((nextViewMode, nextTab = null, nextFolderId = null) => {
    if (!project?.Id) {
      return;
    }
    // Only rewrite the URL while we are STILL on this project's route.
    //
    // Without this, leaving the project was silently undone. This callback has
    // location.search in its deps, so any navigation rebuilds it; that new
    // identity re-fires RSAAnalysis's "report the active tab" effect
    // (RSAAnalysis.js ~9306, deps [activeMainTab, onActiveMainTabChange]), which
    // calls straight back in here and navigates — with replace: true — to
    // /projects/<id>?rsa_view=…&rsa_tab=…. Net effect: you tap Back, the URL
    // becomes /projects for one tick, then gets replaced with the project URL
    // again and the view re-renders in place. It reads as "the button just
    // refreshed the page".
    //
    // It affected every route change away from a project view, not only the
    // bottom bar's Back — the drawer's Projects link went through the same
    // pushState-then-replaced sequence.
    const projectBase = `/projects/${project.Id}`;
    if (location.pathname !== projectBase && location.pathname !== `${projectBase}/view`) {
      return;
    }
    const params = new URLSearchParams(location.search);
    if (nextViewMode === 'analysis') {
      params.set(RSA_VIEW_QUERY_KEY, 'analysis');
      if (nextTab) {
        params.set(RSA_TAB_QUERY_KEY, nextTab);
      } else {
        params.delete(RSA_TAB_QUERY_KEY);
      }
    } else {
      params.set(RSA_VIEW_QUERY_KEY, 'files');
      params.delete(RSA_TAB_QUERY_KEY);
    }
    if (nextFolderId) {
      params.set('folder', nextFolderId);
    } else if (nextViewMode === 'analysis') {
      params.delete('folder');
    }
    const search = params.toString();
    navigate(
      {
        pathname: `/projects/${project.Id}`,
        search: search ? `?${search}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, project?.Id]);

  const handleActiveMainTabChange = React.useCallback((nextTab) => {
    updateRsaRouteState('analysis', nextTab);
  }, [updateRsaRouteState]);

  const handleGoToFiles = React.useCallback((targetFolder = null) => {
    updateRsaRouteState('files', null, targetFolder?.id || null);
    setSelectedFile(null);
    if (targetFolder?.id && project?.Id) {
      const nextFolder = {
        id: targetFolder.id,
        name: targetFolder.name || 'Deliverables',
      };
      setSelectedFolder(nextFolder);
    }
    const flagKey = 'rsa_analysis_back_flyout_shown';
    if (!sessionStorage.getItem(flagKey)) {
      sessionStorage.setItem(flagKey, '1');
      const rect = analysisBtnRef.current?.getBoundingClientRect();
      if (rect) {
        setFlyoutFixedStyle({
          top: rect.top + rect.height / 2,
          right: window.innerWidth - rect.left + 6,
        });
      }
      setShowAnalysisFlyout(true);
      if (flyoutTimerRef.current) clearTimeout(flyoutTimerRef.current);
      flyoutTimerRef.current = setTimeout(() => setShowAnalysisFlyout(false), 4000);
    }
  }, [project?.Id, updateRsaRouteState]);

  // No effect needed — rsaViewMode is derived from URL via useMemo above.

  // Preload RSA setup from central registry (Shared Drive) when project page loads (RSA project types)
  useEffect(() => {
    if (!project || !isRsaProject || !project.Id) {
      setPreloadedRsaSetup(null);
      setPreloadedRsaSetupFolderId(null);
      setPreloadedRsaSetupFolderName(null);
      setPreloadRsaSetupLoading(false);
      return;
    }
    const projectId = String(project.Id);
    const isProd = window.location.hostname.includes('123bimlive.com');
    const baseUrl = (ENVIRONMENT === 'production' || isProd) ? '' : API_BASE_URL;
    const apiBase = baseUrl ? `${API_BASE_URL}/api/google-drive/rsa` : '/api/google-drive/rsa';
    setPreloadRsaSetupLoading(true);
    axios.get(`${apiBase}/registry/setup`, { params: { project_id: projectId } })
      .then((res) => {
        const { setup, folderId, folderName } = res.data || {};
        setPreloadedRsaSetup(setup && typeof setup === 'object' ? setup : null);
        setPreloadedRsaSetupFolderId(folderId || null);
        setPreloadedRsaSetupFolderName(folderName || null);
      })
      .catch(() => {
        setPreloadedRsaSetup(null);
        setPreloadedRsaSetupFolderId(null);
        setPreloadedRsaSetupFolderName(null);
      })
      .finally(() => setPreloadRsaSetupLoading(false));
  }, [project?.Id, project?.ProjectType]);

  // Update breadcrumb when project changes
  useEffect(() => {
    if (project) {
      const nextName = project.Name || 'Project Files';
      setDisplayProjectName(nextName);
      setCurrentPath([{ id: 'root', name: nextName }]);
    }
  }, [project]);

  // Viewer presence tracking for RSA Existing/Model projects: these types have
  // no 3D overlay to click and no separate pixel-streaming /view page, so being
  // on this project's page IS the engagement + presence signal (unlike the
  // visualization project types, where ProjectCard/ProjectViewer register
  // overlay clicks + heartbeats around the 3D viewer instead).
  useEffect(() => {
    if (!isRsaProject || !project?.Id) {
      return undefined;
    }

    const projectId = project.Id;

    viewerPresenceService.registerOverlayClick(projectId).catch((error) => {
      console.warn(`⚠️ ProjectView: Failed to register engagement for project ${projectId}:`, error);
    });

    viewerPresenceService.sendHeartbeat(projectId).catch((error) => {
      console.warn(`⚠️ ProjectView: Failed to send initial heartbeat for project ${projectId}:`, error);
    });

    const heartbeatInterval = setInterval(() => {
      viewerPresenceService.sendHeartbeat(projectId).catch((error) => {
        console.warn(`⚠️ ProjectView: Failed to send heartbeat for project ${projectId}:`, error);
      });
    }, 20000); // 20s; backend timeout is 60s, so a couple of missed beats still forgive network hiccups

    return () => {
      clearInterval(heartbeatInterval);
      viewerPresenceService.leaveProjectView(projectId).catch((error) => {
        console.warn(`⚠️ ProjectView: Failed to notify server of leaving project ${projectId}:`, error);
      });
    };
  }, [isRsaProject, project?.Id]);

  // Also handle page unload (browser close, navigation away) via sendBeacon
  useEffect(() => {
    if (!isRsaProject || !project?.Id) {
      return undefined;
    }

    const handleBeforeUnload = () => {
      const userEmail = sessionStorage.getItem('userEmail');
      if (!userEmail) {
        return;
      }
      try {
        const data = JSON.stringify({ email: userEmail });
        const url = `${window.location.origin}/api/projects/${project.Id}/viewers/leave`;
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } catch (error) {
        console.warn('Failed to send leave notification via beacon:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRsaProject, project?.Id]);

  useEffect(() => {
    if (!showHeaderMenu) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setShowHeaderMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHeaderMenu]);

  useEffect(() => {
    return () => {
      if (flyoutTimerRef.current) clearTimeout(flyoutTimerRef.current);
    };
  }, []);

  // Check for selected files periodically to update download button visibility
  useEffect(() => {
    const checkSelectedFiles = () => {
      const fe = fileExplorerRef.current;
      if (fe && fe.hasSelectedFiles !== undefined) {
        setHasSelectedFiles(fe.hasSelectedFiles);
        setCanDeleteSelection(Boolean(fe.canDeleteSelection));
      }
    };

    // Check immediately and then periodically
    checkSelectedFiles();
    const interval = setInterval(checkSelectedFiles, 100);

    return () => clearInterval(interval);
  }, []);

  const updateBreadcrumb = (newPath) => {
    setCurrentPath((prevPath) => {
      if (!Array.isArray(newPath)) return prevPath;
      if (
        prevPath.length === newPath.length &&
        prevPath.every((item, index) => item?.id === newPath[index]?.id && item?.name === newPath[index]?.name)
      ) {
        return prevPath;
      }
      return newPath;
    });
  };

  if (!project) {
    return (
      <div className="project-view-container">
        <div className="project-view-error">
          <h2>Project not found</h2>
          <button onClick={onBack}>Back to Projects</button>
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Convert to Date object if it's a string
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) return 'N/A';
    
    return dateObj.toLocaleDateString() + ' ' + 
           dateObj.getHours().toString().padStart(2, '0') + ':' + 
           dateObj.getMinutes().toString().padStart(2, '0');
  };

  const isExpired = project.Expiration && new Date(project.Expiration) <= new Date();
  const canView = project.Viewable && !isExpired;
  const hasVirtualTour = project.Code && project.Code !== '';

  const handleFileSelect = (file) => {
    console.log('📁 File selected:', file);
    setSelectedFile(file);
    setSelectedFolder(null);
  };

  const handleFolderSelect = (folder) => {
    console.log('📁 Folder selected:', folder);
    setSelectedFolder(folder);
    setSelectedFile(null);
  };

  const handleBreadcrumbClick = (index, event) => {
    event.stopPropagation(); // Prevent clearing selection when clicking breadcrumbs
    // Navigate to the clicked breadcrumb level
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    
    // If clicking on root (index 0), navigate to root folder
    if (index === 0) {
      setSelectedFolder(null);
      // Update URL to project root
      const url = `/projects/${project.Id}`;
      const state = { folderId: 'root' };
      window.history.pushState(state, '', url);
    } else {
      // Find the folder corresponding to the clicked breadcrumb item
      const targetFolderId = newPath[newPath.length - 1].id;
      // We'll need to pass this to FileExplorer to handle the navigation
      setSelectedFolder({ id: targetFolderId, name: newPath[newPath.length - 1].name });
    }
  };

  const handleClearSelection = () => {
    // This will be passed to FileExplorer to clear file selections
    // The actual implementation is in FileExplorer component
  };

  const handleRefresh = () => {
    // Refresh files via FileExplorer ref
    if (fileExplorerRef.current && fileExplorerRef.current.handleRefresh) {
      fileExplorerRef.current.handleRefresh();
    }
  };

  const handleRsaResyncSourceCaches = async (e) => {
    e.stopPropagation();
    if (!project?.Id || rsaResyncLoading) {
      return;
    }
    setRsaResyncLoading(true);
    try {
      const isProd = window.location.hostname.includes('123bimlive.com');
      const baseUrl = ENVIRONMENT === 'production' || isProd ? '' : API_BASE_URL;
      const url = baseUrl
        ? `${API_BASE_URL}/api/projects/${project.Id}/rsa-resync-source-caches`
        : `/api/projects/${project.Id}/rsa-resync-source-caches`;
      const res = await axios.post(url);
      const d = res.data || {};
      const processed = d.xml_files_processed ?? 0;
      const folderCount = Array.isArray(d.rsa_folders) ? d.rsa_folders.length : 0;
      const errCount = d.error_count ?? 0;
      let msg = `RSA caches resynced: ${processed} XML file(s), ${folderCount} RSA folder(s) under Source.`;
      if (errCount > 0) {
        msg += ` ${errCount} issue(s) reported — check server logs or response payload.`;
      }
      try {
        sessionStorage.setItem(
          `rsaMileageCachesSynced:${project.Id}`,
          String(Date.now())
        );
      } catch (e) {
        /* ignore quota / private mode */
      }
      window.alert(msg);
      window.dispatchEvent(
        new CustomEvent('rsa-mileage-caches-synced', { detail: { projectId: project.Id } })
      );
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        window.alert(
          'RSA resync endpoint was not found (404). Deploy the latest Backend/123bimlive (routes/project_routes.py) to this API host, or use the same base URL as /api/projects/.../files. After deploy, GET the same path should return JSON with endpoint info instead of 404.'
        );
        return;
      }
      const apiMsg = err.response?.data?.error || err.response?.data?.hint;
      window.alert(apiMsg || err.message || 'RSA cache resync failed.');
    } finally {
      setRsaResyncLoading(false);
    }
  };

  const handleRenameProject = async (newName) => {
    const trimmedName = String(newName || '').trim();
    if (!trimmedName || !project?.Id) {
      return;
    }

    setRenameError('');
    setIsRenaming(true);

    try {
      const isProd = window.location.hostname.includes('123bimlive.com');
      const baseUrl = ENVIRONMENT === 'production' || isProd ? '' : API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/projects/${project.Id}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: trimmedName })
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = {};
      }

      if (!response.ok || payload?.success === false) {
        const backendError = payload?.error || payload?.message;
        throw new Error(backendError || 'Failed to rename project');
      }

      setDisplayProjectName(trimmedName);
      clearProjectFileExplorerCache(project.Id);
      fileExplorerRef.current?.renameRootFolder?.(trimmedName);
      setCurrentPath((prevPath) => {
        if (!Array.isArray(prevPath) || prevPath.length === 0) {
          return [{ id: 'root', name: trimmedName }];
        }
        return prevPath.map((item, index) => (
          index === 0 ? { ...item, name: trimmedName } : item
        ));
      });
      setShowRenameModal(false);
      setRenameError('');
    } catch (error) {
      console.error(`Error renaming project ${project.Id}:`, error);
      setRenameError(error.message || 'Failed to rename project');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleHeaderMenuAction = (action, event) => {
    event?.stopPropagation?.();
    setShowHeaderMenu(false);

    switch (action) {
      case 'manageUsers':
        setShowManageUsersModal(true);
        break;
      case 'rename':
        setRenameError('');
        setShowRenameModal(true);
        break;
      case 'leave':
        setShowLeaveProjectModal(true);
        break;
      default:
        break;
    }
  };

  const handleLeaveProject = async () => {
    if (!project?.Id) {
      return;
    }
    if (!currentUserEmail) {
      window.alert('Could not determine the current user email.');
      return;
    }
    if (isLeavingProject) {
      return;
    }

    setIsLeavingProject(true);
    try {
      const isProd = window.location.hostname.includes('123bimlive.com');
      const baseUrl = ENVIRONMENT === 'production' || isProd ? '' : API_BASE_URL;
      // Identity comes from the verified idToken; the body email is ignored
      // by the backend (kept for older deployments during rollout).
      const response = await fetch(`${baseUrl}/api/projects/${project.Id}/leave`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ email: currentUserEmail })
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = {};
      }

      if (!response.ok || payload?.success === false) {
        const backendError = payload?.error || payload?.message;
        throw new Error(backendError || 'Failed to leave project');
      }

      setShowLeaveProjectModal(false);
      onBack?.();
    } catch (error) {
      console.error(`Error leaving project ${project.Id}:`, error);
      window.alert(error.message || 'Failed to leave project.');
    } finally {
      setIsLeavingProject(false);
    }
  };

  const headerMenuButton = (
    <div className="project-view-header-menu" ref={headerMenuRef}>
      <button
        className="menu-trigger-header"
        onClick={(e) => {
          e.stopPropagation();
          setShowHeaderMenu((prev) => !prev);
        }}
        title="Project menu"
        aria-haspopup="menu"
        aria-expanded={showHeaderMenu ? 'true' : 'false'}
      >
        <i className="fas fa-ellipsis-v"></i>
      </button>
      {showHeaderMenu && (
        <div className="project-menu-dropdown project-view-menu-dropdown">
          <ul className="project-menu-list" role="menu">
            <li role="none">
              <button onClick={(e) => handleHeaderMenuAction('manageUsers', e)} role="menuitem">
                <i className="fas fa-users-cog"></i>
                Manage Project Users
              </button>
            </li>
            <li role="none">
              <button onClick={(e) => handleHeaderMenuAction('rename', e)} role="menuitem">
                <i className="fas fa-edit"></i>
                Rename Project
              </button>
            </li>
            <li role="none">
              <button onClick={(e) => handleHeaderMenuAction('leave', e)} role="menuitem" className="danger">
                <i className="fas fa-sign-out-alt"></i>
                Leave Project
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );

  const handleDownload = () => {
    // Trigger download via FileExplorer ref
    if (fileExplorerRef.current && fileExplorerRef.current.handleDownload) {
      fileExplorerRef.current.handleDownload();
    }
  };

  const handleExplorerDelete = () => {
    if (fileExplorerRef.current?.handleDelete) {
      fileExplorerRef.current.handleDelete();
    }
  };

  const handleNewFolder = () => {
    if (fileExplorerRef.current?.startInlineNewFolder) {
      fileExplorerRef.current.startInlineNewFolder();
    } else {
      console.warn('FileExplorer ref missing startInlineNewFolder');
    }
  };

  const previousBreadcrumbStep = currentPath.length > 1
    ? currentPath[currentPath.length - 2]
    : null;
  const previousBreadcrumbTooltip = previousBreadcrumbStep?.name
    ? `Back to ${previousBreadcrumbStep.name}`
    : 'Back';

  return (
    <div className="project-view-container">
      {/* Project Header */}
      <div 
        className="project-view-header"
        onClick={handleClearSelection}
      >
        <div className="project-view-header-left">
          <div className="project-view-header-top">
            <button className="back-btn" onClick={onBack} title="Back to Projects">
              <i className="fas fa-arrow-left"></i>
              Projects
            </button>
            <span className="project-role-badge">{project.UserRole}</span>
            <div className="project-meta-info">
              {/* "Project " hides on phones, leaving "ID: 2466" — the chip row
                  must survive a 4-5 digit id on a 320px screen. */}
              <span><span className="meta-id-prefix">Project </span>ID: {project.Id}</span>
              {project.UserRoleId < 2 && (
                <span className="project-meta-type">Type: {project.Plan}</span>
              )}
              <ActiveViewersBadge projectId={project.Id} variant="inline" />
            </div>
          </div>
          <div
            className="project-view-header-info"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="breadcrumb-path project-view-header-breadcrumb">
              {currentPath.map((pathItem, index) => (
                <React.Fragment key={pathItem.id}>
                  <span
                    className="breadcrumb-item"
                    onClick={(e) => handleBreadcrumbClick(index, e)}
                  >
                    {pathItem.name}
                  </span>
                  {index < currentPath.length - 1 && (
                    <i className="fas fa-chevron-right breadcrumb-separator" aria-hidden />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        
        <div className="project-actions-header">
          {(project.ProjectType === 'rsa-existing' || project.ProjectType === 'rsa-model') ? (
            /* RSA Existing & RSA Model: Analysis first (primary), then Files */
            <>
              <div className="analysis-btn-wrapper" ref={analysisBtnRef}>
                <button
                  className={`header-action-btn primary ${rsaViewMode === 'analysis' ? 'active' : ''}`}
                  onClick={() => {
                    updateRsaRouteState('analysis', routeRsaTab || (project.ProjectType === 'rsa-model' ? 'upload' : 'setup'));
                  }}
                  title="Analysis"
                >
                  <i className="fas fa-chart-line"></i>
                  Analysis
                </button>
                {showAnalysisFlyout && flyoutFixedStyle && (
                  <div
                    className="analysis-back-flyout"
                    style={{
                      position: 'fixed',
                      top: flyoutFixedStyle.top,
                      right: flyoutFixedStyle.right,
                      transform: 'translateY(-50%)',
                    }}
                  >
                    Click here to go back to Analysis Dashboard page
                    <i className="fas fa-arrow-right" aria-hidden />
                  </div>
                )}
              </div>
                <button
                  className={`header-action-btn secondary ${rsaViewMode === 'files' ? 'active' : ''}`}
                  onClick={() => {
                    updateRsaRouteState('files');
                    const el = document.getElementById('project-files-section');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  title="Project Files"
                >
                  <i className="fas fa-folder-open"></i>
                  Files
                </button>
              {headerMenuButton}
            </>
          ) : (
            /* Visualization: full header actions (3D View, Virtual Tour, etc.) */
            <>
              {canView && (
                <button 
                  className="header-action-btn primary"
                  onClick={async () => {
                    console.log(`🎯 3D View button clicked for project ${project.Id}!`);
                    try {
                      await viewerPresenceService.registerOverlayClick(project.Id);
                      console.log(`✅ Overlay click registered for project ${project.Id}`);
                    } catch (error) {
                      console.warn(`⚠️ Failed to register overlay click for project ${project.Id}:`, error);
                    }
                    onViewProject(project);
                  }}
                >
                  <i className="fas fa-cube"></i>
                  3D View
                </button>
              )}
              {hasVirtualTour ? (
                <>
                  <VirtualTourButton project={project} />
                  <button className="header-action-btn tertiary">
                    <i className="fas fa-link"></i>
                    VTour Link
                  </button>
                </>
              ) : (
                <>
                  <button className="header-action-btn disabled">
                    <i className="fas fa-vr-cardboard"></i>
                    Virtual Tour
                  </button>
                  <button className="header-action-btn disabled">
                    <i className="fas fa-link"></i>
                    VTour Link
                  </button>
                </>
              )}
              {headerMenuButton}
            </>
          )}
        </div>
      </div>
      
      <ManageProjectUsersModal
        projectId={project.Id}
        projectName={displayProjectName}
        isOpen={showManageUsersModal}
        onClose={() => setShowManageUsersModal(false)}
      />

      <RenameProjectModal
        projectName={displayProjectName}
        initialName={displayProjectName}
        isOpen={showRenameModal}
        isSubmitting={isRenaming}
        error={renameError}
        onCancel={() => {
          if (isRenaming) {
            return;
          }
          setShowRenameModal(false);
          setRenameError('');
        }}
        onConfirm={handleRenameProject}
      />

      <LeaveProjectModal
        projectName={displayProjectName}
        isOpen={showLeaveProjectModal}
        isSubmitting={isLeavingProject}
        onCancel={() => {
          if (isLeavingProject) {
            return;
          }
          setShowLeaveProjectModal(false);
        }}
        onConfirm={handleLeaveProject}
      />

      {/* Project Content */}
      {(project.ProjectType === 'rsa-existing' || project.ProjectType === 'rsa-model') && rsaViewMode === 'analysis' ? (
        <div
          className={`project-content rsa-analysis-content ${project.ProjectType === 'rsa-model' ? 'rsa-model-content' : ''}`}
          data-project-type={project.ProjectType}
        >
          {project.ProjectType === 'rsa-model' ? (
            <RSAModelAnalysis
              projectId={project.Id}
              preloadedRsaSetup={preloadedRsaSetup}
              preloadedRsaSetupFolderId={preloadedRsaSetupFolderId}
              preloadedRsaSetupFolderName={preloadedRsaSetupFolderName}
              preloadRsaSetupLoading={preloadRsaSetupLoading}
              initialActiveMainTab={initialRsaTab}
              initialSelectedRunId={deepLinkRunId}
              onActiveMainTabChange={handleActiveMainTabChange}
              onGoToFiles={handleGoToFiles}
            />
          ) : (
            <RSAExistingAnalysis
              projectId={project.Id}
              // The project's display name, straight from the loaded project
              // (2026-09-09): RSAAnalysis' own project list comes from
              // /api/projectsSearch, which reads a different projects table and
              // does not contain e.g. 2487 "MassDOT US7" — runs and Deliverables
              // downloads were falling back to "Project 2487".
              projectName={project.Name}
              preloadedRsaSetup={preloadedRsaSetup}
              preloadedRsaSetupFolderId={preloadedRsaSetupFolderId}
              preloadedRsaSetupFolderName={preloadedRsaSetupFolderName}
              preloadRsaSetupLoading={preloadRsaSetupLoading}
              initialActiveMainTab={initialRsaTab}
              initialSelectedRunId={deepLinkRunId}
              onActiveMainTabChange={handleActiveMainTabChange}
              onGoToFiles={handleGoToFiles}
            />
          )}
        </div>
      ) : (
        <div
          id={(project.ProjectType === 'rsa-existing' || project.ProjectType === 'rsa-model') ? 'project-files-section' : undefined}
          className="project-content"
        >
          {/* Project Data Section */}
          {projectData && (
            <div className="project-data-section">
              <h3>Project Data</h3>
              <div className="project-data-grid">
                <div className="data-item">
                  <span className="data-label">Project ID:</span>
                  <span className="data-value">{projectData.project_id}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">Project Code:</span>
                  <span className="data-value">{projectData.project_code}</span>
                </div>
                {projectData.files && projectData.files.length > 0 && (
                  <div className="data-item">
                    <span className="data-label">Files:</span>
                    <span className="data-value">{projectData.files.length}</span>
                  </div>
                )}
                {projectData.tasks && projectData.tasks.length > 0 && (
                  <div className="data-item">
                    <span className="data-label">Tasks:</span>
                    <span className="data-value">{projectData.tasks.length}</span>
                  </div>
                )}
                {projectData.reports && projectData.reports.length > 0 && (
                  <div className="data-item">
                    <span className="data-label">Reports:</span>
                    <span className="data-value">{projectData.reports.length}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="project-tree-panel">
              <FileExplorer 
              ref={fileExplorerRef}
              project={project}
              onFileSelect={handleFileSelect}
              onFolderSelect={handleFolderSelect}
              currentPath={currentPath}
              onBreadcrumbUpdate={updateBreadcrumb}
              selectedFolder={selectedFolder}
              onClearSelection={handleClearSelection}
              showUploadModal={showUploadModal}
              onOpenUploadModal={() => setShowUploadModal(true)}
              onCloseUploadModal={() => setShowUploadModal(false)}
              currentFolderId={selectedFolder && selectedFolder.id !== 'root' ? selectedFolder.id : project.Id}
              fileListToolbar={
                <div className="breadcrumb-actions">
                  {previousBreadcrumbStep && (
                    <button
                      type="button"
                      className="file-action-btn file-action-btn-back"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBreadcrumbClick(currentPath.length - 2, e);
                      }}
                      title={previousBreadcrumbTooltip}
                      aria-label={previousBreadcrumbTooltip}
                    >
                      <i className="fas fa-arrow-left" aria-hidden />
                    </button>
                  )}
                  <button 
                    className="file-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefresh();
                    }}
                    title="Refresh"
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>
                  <PermissionGate group="Admin" fallback={null}>
                    {isRsaProject && (
                      <button
                        type="button"
                        className="file-action-btn rsa-resync-cache-btn"
                        onClick={handleRsaResyncSourceCaches}
                        disabled={rsaResyncLoading}
                        title="Resync RSA mileage &amp; ISD MySQL caches from current Source → RSA folders (developers)"
                      >
                        <i
                          className={`fas fa-database${rsaResyncLoading ? ' fa-spin' : ''}`}
                          aria-hidden
                        />
                      </button>
                    )}
                    </PermissionGate>
                    <button 
                      className="file-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNewFolder();
                      }}
                      title="New Folder"
                    >
                      <i className="fas fa-folder-plus"></i>
                    </button>
                  <button 
                    className="file-action-btn upload-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUploadModal(true);
                    }}
                    title="Upload"
                  >
                    <i className="fas fa-upload"></i>
                  </button>
                  {canDeleteSelection && (
                    <button
                      type="button"
                      className="file-action-btn file-action-btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExplorerDelete();
                      }}
                      title="Delete"
                    >
                      <i className="fas fa-trash" aria-hidden />
                    </button>
                  )}
                  {hasSelectedFiles && (
                    <button 
                      className="file-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload();
                      }}
                      title="Download"
                    >
                      <i className="fas fa-download"></i>
                    </button>
                  )}
                </div>
              }
            />
          </div>
        </div>
      )}

    </div>
  );
};

// Virtual Tour Button Component
const VirtualTourButton = ({ project }) => {
  const [virtualTourCode, setVirtualTourCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load virtual tour code on component mount
  useEffect(() => {
    const loadVirtualTourCode = async () => {
      try {
        const code = await getVirtualTourCode(project.Id);
        setVirtualTourCode(code);
      } catch (error) {
        console.error('Error loading virtual tour code:', error);
      }
    };
    
    loadVirtualTourCode();
  }, [project.Id]);

  const handleVirtualTourClick = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const virtualTourUrl = await getVirtualTourUrl(project.Id);
      console.log('🔗 Opening virtual tour URL:', virtualTourUrl);
      window.open(virtualTourUrl, '_blank');
    } catch (error) {
      console.error('Error getting virtual tour URL:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button 
      className="header-action-btn tertiary"
      onClick={handleVirtualTourClick}
      disabled={isLoading}
      title={virtualTourCode ? `Virtual Tour (${virtualTourCode})` : 'Virtual Tour'}
    >
      <i className="fas fa-vr-cardboard"></i>
      {isLoading ? 'Loading...' : `VTOUR ${virtualTourCode ? `(${virtualTourCode})` : ''}`}
    </button>
  );
};

export default ProjectView;
